/**
 * voice-v8 — Voll-Bild Pan/Zoom-Overlay für ein (inline) SVG.
 *
 * Für Detailansichten (z.B. Human-Design-Bodygraph): das SVG bleibt VEKTOR und
 * damit gestochen scharf bis weit hineingezoomt — anders als ein gerastertes
 * `<img>`. Mausrad/Doppelklick zoomt (auf den Cursor zentriert), Ziehen pant,
 * zwei Finger = Pinch. Esc / Klick auf den Rand / Schließen-Knopf schließt.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 10;

export function openSvgZoom(svgMarkup: string, title = ""): void {
  const overlay = document.createElement("div");
  overlay.className = "svgzoom-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", title || "Vergrößerte Ansicht");

  const stage = document.createElement("div");
  stage.className = "svgzoom-stage";
  const content = document.createElement("div");
  content.className = "svgzoom-content";
  content.innerHTML = svgMarkup;
  const svg = content.querySelector("svg");
  if (svg) {
    svg.removeAttribute("width");
    svg.removeAttribute("height");
  }
  stage.appendChild(content);
  overlay.appendChild(stage);

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "svgzoom-close";
  closeBtn.setAttribute("aria-label", "Schließen");
  closeBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71 12 12.01l-6.3-6.3-1.4 1.41 6.29 6.3-6.3 6.29 1.41 1.41 6.3-6.29 6.29 6.3 1.41-1.41-6.29-6.3 6.29-6.29z"/></svg>';
  overlay.appendChild(closeBtn);

  const hint = document.createElement("div");
  hint.className = "svgzoom-hint";
  hint.textContent =
    "Ziehen zum Verschieben · Mausrad / Pinch zoomt · Doppelklick";
  overlay.appendChild(hint);

  let scale = 1;
  let tx = 0;
  let ty = 0;
  const apply = (): void => {
    content.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  };

  // Zoom auf einen Bühnen-Punkt (cx,cy relativ zur Stage) zentrieren.
  const zoomAt = (cx: number, cy: number, factor: number): void => {
    const prev = scale;
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor));
    const k = scale / prev;
    tx = cx - k * (cx - tx);
    ty = cy - k * (cy - ty);
    if (scale <= MIN_SCALE) {
      tx = 0;
      ty = 0;
    }
    apply();
  };

  stage.addEventListener(
    "wheel",
    (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      zoomAt(
        e.clientX - rect.left,
        e.clientY - rect.top,
        e.deltaY < 0 ? 1.15 : 1 / 1.15,
      );
    },
    { passive: false },
  );

  // Pointer: Ziehen (1 Finger/Maus) + Pinch (2 Finger).
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  const dist = (): number => {
    const pts = [...pointers.values()];
    return Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
  };
  const mid = (): { x: number; y: number } => {
    const pts = [...pointers.values()];
    return { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
  };

  stage.addEventListener("pointerdown", (e: PointerEvent) => {
    stage.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) pinchDist = dist();
    stage.classList.add("is-grabbing");
  });
  stage.addEventListener("pointermove", (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    const prev = pointers.get(e.pointerId)!;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      tx += e.clientX - prev.x;
      ty += e.clientY - prev.y;
      apply();
    } else if (pointers.size === 2 && pinchDist > 0) {
      const rect = stage.getBoundingClientRect();
      const m = mid();
      const d = dist();
      zoomAt(m.x - rect.left, m.y - rect.top, d / pinchDist);
      pinchDist = d;
    }
  });
  const endPointer = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchDist = 0;
    if (pointers.size === 0) stage.classList.remove("is-grabbing");
  };
  stage.addEventListener("pointerup", endPointer);
  stage.addEventListener("pointercancel", endPointer);

  stage.addEventListener("dblclick", (e: MouseEvent) => {
    const rect = stage.getBoundingClientRect();
    if (scale > 1.05) {
      scale = 1;
      tx = 0;
      ty = 0;
      apply();
    } else {
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, 2.5);
    }
  });

  const close = (): void => {
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  closeBtn.addEventListener("click", close);
  // Klick auf den Rand (nicht aufs Chart) schließt.
  overlay.addEventListener("pointerdown", (e) => {
    if (e.target === overlay) close();
  });

  document.body.appendChild(overlay);
  apply();
}
